// Hjælpefunktioner til at arbejde med flade lister, der har parent_id-relation.

type WithIdAndParent = { id: number; parentId: number | null };

export type TreeNode<T> = T & { depth: number };

/**
 * Sorterer en flad liste hierarkisk: rod-elementer først, så deres børn under
 * dem (med depth-niveau angivet), rekursivt. Bruges til at rendere et tree i
 * en almindelig liste eller dropdown med indrykning.
 */
export function flattenTree<T extends WithIdAndParent>(items: T[]): TreeNode<T>[] {
  const byParent = new Map<number | null, T[]>();
  for (const item of items) {
    const key = item.parentId ?? null;
    const arr = byParent.get(key) ?? [];
    arr.push(item);
    byParent.set(key, arr);
  }
  for (const [, arr] of byParent) {
    arr.sort((a, b) =>
      ((a as unknown as { sortOrder?: number }).sortOrder ?? 0) -
        ((b as unknown as { sortOrder?: number }).sortOrder ?? 0) ||
      ((a as unknown as { name?: string }).name ?? '').localeCompare(
        (b as unknown as { name?: string }).name ?? '',
        'da',
      ),
    );
  }
  const result: TreeNode<T>[] = [];
  function visit(parentId: number | null, depth: number) {
    const children = byParent.get(parentId) ?? [];
    for (const child of children) {
      result.push({ ...child, depth });
      visit(child.id, depth + 1);
    }
  }
  visit(null, 0);
  return result;
}

/**
 * Returnerer kun id'er for items der ikke kan vælges som forælder for `selfId`
 * (sig selv + alle efterkommere) — bruges til at filtrere parent-dropdowns
 * ved redigering, så man ikke laver en cyklus.
 */
export function descendantsOf<T extends WithIdAndParent>(
  items: T[],
  selfId: number,
): Set<number> {
  const result = new Set<number>([selfId]);
  let added = true;
  while (added) {
    added = false;
    for (const item of items) {
      if (item.parentId !== null && result.has(item.parentId) && !result.has(item.id)) {
        result.add(item.id);
        added = true;
      }
    }
  }
  return result;
}
